import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { supabase } from '../../lib/supabase';
import { collegeJourneyService } from '../../lib/college-journey-service';
import timelineService from '../../services/timelineService';
import { toast } from 'react-hot-toast';
import {
  DollarSign,
  CheckCircle,
  Circle,
  Calendar,
  ExternalLink,
  Search,
  Award,
  Plus,
  ChevronRight,
  AlertCircle,
  Loader2,
  FileText,
  ArrowLeft,
  Sparkles,
  Target,
  TrendingUp,
  Clock,
  ThumbsDown,
  Star,
  X
} from 'lucide-react';

// Scholarship Card Component
const ScholarshipCard = ({ scholarship, onStatusChange, currentStatus }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const statusButtons = [
    { 
      status: 'not_interested', 
      icon: ThumbsDown, 
      label: 'Not Interested',
      color: 'text-red-600 hover:bg-red-50'
    },
    { 
      status: 'interested', 
      icon: Star, 
      label: 'Interested',
      color: 'text-yellow-600 hover:bg-yellow-50'
    },
    { 
      status: 'selected', 
      icon: CheckCircle, 
      label: 'Apply',
      color: 'text-emerald-600 hover:bg-emerald-50'
    }
  ];
  
  const getMatchScoreBadge = (score) => {
    if (!score) return null;
    const percentage = Math.round(score * 100);
    let color = 'bg-stone-100 text-slate-700';
    if (score >= 0.8) color = 'bg-emerald-100 text-emerald-700';
    else if (score >= 0.6) color = 'bg-yellow-100 text-yellow-700';
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        {percentage}% match
      </span>
    );
  };
  
  return (
    <div className={`p-4 rounded-lg border transition-all ${
      currentStatus === 'selected' ? 'bg-emerald-50 border-emerald-300' :
      currentStatus === 'interested' ? 'bg-yellow-50 border-yellow-300' :
      'bg-white border-stone-200'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h4 className="font-medium text-slate-900">{scholarship.name}</h4>
              {scholarship.provider && (
                <p className="text-sm text-slate-600 mt-1">{scholarship.provider}</p>
              )}
            </div>
            {scholarship.ai_match_score && getMatchScoreBadge(scholarship.ai_match_score)}
          </div>
          
          <div className="flex items-center gap-4 mt-3 text-sm">
            <span className="text-emerald-600 font-semibold flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              {scholarship.amount}
            </span>
            {scholarship.deadline && (
              <span className="text-slate-600 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Due: {new Date(scholarship.deadline).toLocaleDateString()}
              </span>
            )}
            {scholarship.application_link && (
              <a
                href={scholarship.application_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-azure-600 hover:text-azure-700 flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="w-3 h-3" />
                Apply
              </a>
            )}
          </div>
          
          {/* Expandable details */}
          {(scholarship.requirements || scholarship.match_reason) && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-azure-600 hover:text-azure-700 mt-2 flex items-center gap-1"
            >
              {isExpanded ? 'Show less' : 'Show details'}
              <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
            </button>
          )}
          
          {isExpanded && (
            <div className="mt-3 space-y-2 text-sm text-slate-600">
              {scholarship.requirements && (
                <div>
                  <span className="font-medium">Requirements:</span>
                  <p className="mt-1">{scholarship.requirements}</p>
                </div>
              )}
              {scholarship.match_reason && (
                <div>
                  <span className="font-medium">Why this matches:</span>
                  <p className="mt-1">{scholarship.match_reason}</p>
                </div>
              )}
              {scholarship.notes && (
                <div>
                  <span className="font-medium">Notes:</span>
                  <p className="mt-1">{scholarship.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Status buttons */}
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-stone-100">
        {statusButtons.map(({ status, icon: Icon, label, color }) => (
          <button
            key={status}
            onClick={() => onStatusChange(scholarship.id, status)}
            className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm transition-colors ${
              currentStatus === status 
                ? `${color} bg-opacity-20 font-medium` 
                : `${color} hover:bg-opacity-10`
            }`}
          >
            <Icon className="w-3 h-3" />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

const FinancialAidHub = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useFamilyContext();
  const { colleges } = useCollegeContext();

  // Determine which view to show based on URL param
  const viewParam = searchParams.get('view');
  const activeView = viewParam === 'scholarships' ? 'scholarships' : 'financial-aid';
  
  // Helper function to calculate date 7 days before
  const getResearchDate = (deadlineDate) => {
    if (!deadlineDate) return null;
    const deadline = new Date(deadlineDate);
    const researchDate = new Date(deadline);
    researchDate.setDate(deadline.getDate() - 7);
    return researchDate.toISOString().split('T')[0]; // Return YYYY-MM-DD format
  };
  
  // Helper function to extract scholarship amount as number for filtering
  const parseScholarshipAmount = (amountStr) => {
    if (!amountStr) return 0;
    
    const lowerAmount = amountStr.toLowerCase();
    
    // Exclude clearly large scholarships
    if (lowerAmount.includes('full tuition') || 
        lowerAmount.includes('full ride') || 
        lowerAmount.includes('4 years') ||
        lowerAmount.includes('per year') ||
        lowerAmount.includes('annual')) {
      return 999999; // Return large number to exclude from low-dollar category
    }
    
    // Extract numbers from string like "$1,000" or "$500-$2,000"
    const numbers = amountStr.match(/\d+,?\d*/g);
    if (!numbers || numbers.length === 0) return 0;
    
    // Take the first number (or minimum if range)
    const amount = parseInt(numbers[0].replace(/,/g, ''));
    
    // If it's clearly per year (like $12,500/year), exclude it
    if (amount > 5000 && (lowerAmount.includes('/year') || lowerAmount.includes('yearly'))) {
      return 999999;
    }
    
    return amount;
  };
  
  const [financialAidTasks, setFinancialAidTasks] = useState([]);
  const [scholarships, setScholarships] = useState([]);
  const [recommendedScholarships, setRecommendedScholarships] = useState([]);
  const [groupedScholarships, setGroupedScholarships] = useState({
    selected: [],
    interested: [],
    suggested: [],
    not_interested: [],
    lowDollar: []
  });
  const [userPreferences, setUserPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchingMajorScholarships, setSearchingMajorScholarships] = useState(false);
  const [searchingSmallScholarships, setSearchingSmallScholarships] = useState(false);
  const [addingCustomTask, setAddingCustomTask] = useState(false);
  const [customTaskTitle, setCustomTaskTitle] = useState('');
  const [customTaskDate, setCustomTaskDate] = useState('');
  
  // Load financial aid tasks from timeline_items
  useEffect(() => {
    loadFinancialAidTasks();
    loadScholarships();
    loadScholarshipsFromDatabase();
  }, [currentUser]);
  
  const loadFinancialAidTasks = async () => {
    if (!currentUser?.id) return;
    
    try {
      setLoading(true);
      
      // Get financial aid tasks from timeline_items
      const { data: tasks, error } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', currentUser.id)
        .eq('category', 'financial_aid')
        .order('due_date', { ascending: true });
      
      if (error) throw error;
      
      // Add any standard tasks that don't exist yet
      const standardTasks = getStandardFinancialAidTasks();
      const existingTitles = tasks.map(t => t.title.toLowerCase());
      
      const missingTasks = standardTasks.filter(
        task => !existingTitles.includes(task.title.toLowerCase())
      );
      
      // Combine existing and standard tasks
      setFinancialAidTasks([...tasks, ...missingTasks]);
      
    } catch (error) {
      console.error('Error loading financial aid tasks:', error);
      toast.error('Failed to load financial aid tasks');
    } finally {
      setLoading(false);
    }
  };
  
  const getStandardFinancialAidTasks = () => {
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    
    return [
      {
        id: 'fafsa-standard',
        title: 'Complete FAFSA',
        due_date: `${currentYear}-10-01`,
        category: 'financial_aid',
        status: 'pending',
        external_link: 'https://studentaid.gov/fafsa',
        is_standard: true
      },
      {
        id: 'css-standard',
        title: 'Submit CSS Profile',
        due_date: `${currentYear}-11-15`,
        category: 'financial_aid',
        status: 'pending',
        external_link: 'https://cssprofile.collegeboard.org/',
        is_standard: true
      },
      {
        id: 'idoc-standard',
        title: 'Upload IDOC Documents',
        due_date: `${nextYear}-02-01`,
        category: 'financial_aid',
        status: 'pending',
        external_link: 'https://idoc.collegeboard.org/',
        is_standard: true
      }
    ];
  };
  
  const loadScholarships = async () => {
    if (!currentUser?.id) return;
    
    try {
      // Load tracked scholarships from database
      const { data, error } = await supabase
        .from('scholarship_tracking')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('deadline', { ascending: true });
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error loading scholarships:', error);
      }
      
      setScholarships(data || []);
    } catch (error) {
      console.error('Error loading scholarships:', error);
    }
  };
  
  const toggleTaskCompletion = async (task) => {
    if (task.is_standard) {
      // For standard tasks, create a real timeline item
      try {
        const { error } = await supabase
          .from('timeline_items')
          .insert({
            user_id: currentUser.id,
            title: task.title,
            due_date: task.due_date,
            category: 'financial_aid',
            workflow_phase: 'complete',
            completion_status: 'complete'
          });
        
        if (error) throw error;
        
        toast.success(`${task.title} marked as complete`);
        loadFinancialAidTasks();
      } catch (error) {
        console.error('Error creating task:', error);
        toast.error('Failed to update task');
      }
    } else {
      // For existing tasks, update their status
      try {
        const newStatus = task.workflow_phase === 'complete' ? 'ready' : 'complete';
        
        const { error } = await supabase
          .from('timeline_items')
          .update({
            workflow_phase: newStatus,
            completion_status: newStatus === 'complete' ? 'complete' : 'pending'
          })
          .eq('id', task.id);
        
        if (error) throw error;
        
        toast.success(`${task.title} ${newStatus === 'complete' ? 'completed' : 'reopened'}`);
        loadFinancialAidTasks();
      } catch (error) {
        console.error('Error updating task:', error);
        toast.error('Failed to update task');
      }
    }
  };
  
  const addCustomTask = async () => {
    if (!customTaskTitle || !customTaskDate) {
      toast.error('Please enter both title and due date');
      return;
    }
    
    try {
      const { error } = await supabase
        .from('timeline_items')
        .insert({
          user_id: currentUser.id,
          title: customTaskTitle,
          due_date: customTaskDate,
          category: 'financial_aid',
          workflow_phase: 'ready',
          completion_status: 'pending',
          source_type: 'manual'
        });
      
      if (error) throw error;
      
      toast.success('Task added successfully');
      setCustomTaskTitle('');
      setCustomTaskDate('');
      setAddingCustomTask(false);
      loadFinancialAidTasks();
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error('Failed to add task');
    }
  };
  
  // Helper function to categorize scholarships
  const categorizeScholarship = (scholarship) => {
    const name = scholarship.name?.toLowerCase() || '';
    const requirements = scholarship.requirements?.toLowerCase() || '';
    const provider = scholarship.provider?.toLowerCase() || '';
    
    if (name.includes('merit') || requirements.includes('gpa')) return 'merit';
    if (name.includes('need') || requirements.includes('pell')) return 'need';
    if (name.includes('stem') || name.includes('engineer') || name.includes('science')) return 'stem';
    if (name.includes('diversity') || requirements.includes('minority')) return 'diversity';
    if (provider.includes('local') || provider.includes('community')) return 'local';
    if (provider.includes('corporation') || provider.includes('company')) return 'corporate';
    if (provider.includes('foundation')) return 'foundation';
    return 'other';
  };
  
  // Load scholarships from database
  const loadScholarshipsFromDatabase = async () => {
    try {
      // Get user's scholarship preferences
      const { data: preferences } = await supabase
        .from('scholarship_preferences')
        .select('*')
        .eq('user_id', currentUser.id)
        .single();
      
      // Load all scholarships for this user
      const { data: scholarships, error } = await supabase
        .from('scholarships')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('ai_match_score', { ascending: false })
        .order('deadline', { ascending: true });
      
      if (error) throw error;
      
      // Separate low-dollar scholarships (<=1000) first
      const lowDollarScholarships = scholarships?.filter(scholarship => {
        const amount = parseScholarshipAmount(scholarship.amount);
        return amount > 0 && amount <= 1000 && scholarship.status !== 'not_interested';
      }) || [];
      
      // Create a set of low-dollar scholarship IDs for exclusion
      const lowDollarIds = new Set(lowDollarScholarships.map(s => s.id));
      
      // Group remaining scholarships by status (excluding low-dollar ones)
      const grouped = {
        selected: [],
        interested: [],
        suggested: [],
        not_interested: [],
        lowDollar: lowDollarScholarships
      };
      
      scholarships?.forEach(scholarship => {
        // Skip if this is a low-dollar scholarship (it goes only in the special section)
        if (lowDollarIds.has(scholarship.id)) {
          return;
        }
        
        const status = scholarship.status || 'suggested';
        if (grouped[status]) {
          grouped[status].push(scholarship);
        }
      });
      
      setRecommendedScholarships(scholarships || []);
      setGroupedScholarships(grouped);
      setUserPreferences(preferences);
      
    } catch (error) {
      console.error('Error loading scholarships:', error);
    }
  };
  
  // Update scholarship status
  const updateScholarshipStatus = async (scholarshipId, newStatus) => {
    try {
      // First get the scholarship data
      const { data: scholarship, error: fetchError } = await supabase
        .from('scholarships')
        .select('*')
        .eq('id', scholarshipId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Update scholarship status
      const { error } = await supabase
        .from('scholarships')
        .update({ 
          status: newStatus,
          status_updated_at: new Date().toISOString()
        })
        .eq('id', scholarshipId);
      
      if (error) throw error;
      
      // Add timeline items based on new status
      if (newStatus === 'interested' && scholarship.deadline) {
        // Add research reminder 1 week before deadline
        const researchDate = getResearchDate(scholarship.deadline);
        if (researchDate) {
          const researchItem = {
            user_id: currentUser.id,
            source_type: 'reminder',
            source_id: scholarshipId,
            title: `Research ${scholarship.name}`,
            description: `Research and prepare application materials for ${scholarship.name} scholarship`,
            scheduled_date: researchDate,
            category: 'financial_aid',
            priority: 'medium',
            status: 'planned'
          };
          
          await timelineService.upsertTimelineItem(researchItem);
          console.log('Added research reminder to timeline');
        }
      } else if (newStatus === 'selected' && scholarship.deadline) {
        // Add application deadline to timeline
        const applicationItem = {
          user_id: currentUser.id,
          source_type: 'deadline',
          source_id: scholarshipId,
          title: `Apply for ${scholarship.name}`,
          description: `Submit application for ${scholarship.name} scholarship (${scholarship.amount})`,
          scheduled_date: scholarship.deadline,
          category: 'financial_aid',
          priority: 'high',
          status: 'planned'
        };
        
        await timelineService.upsertTimelineItem(applicationItem);
        console.log('Added application deadline to timeline');
      }
      
      // Reload scholarships to reflect changes
      await loadScholarshipsFromDatabase();
      
      // Show appropriate toast message
      const messages = {
        'not_interested': 'Marked as not interested',
        'interested': 'Added to interested list and research reminder to calendar',
        'selected': 'Added to application list and deadline to calendar',
        'applied': 'Marked as applied'
      };
      
      toast.success(messages[newStatus] || 'Status updated');
      
    } catch (error) {
      console.error('Error updating scholarship status:', error);
      toast.error('Failed to update status');
    }
  };
  
  const findScholarshipsWithAI = async () => {
    setSearchingMajorScholarships(true);
    
    try {
      // Get student profile data
      const { data: profile } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', currentUser.id)
        .single();
      
      // Create AI prompt
      const prompt = `Based on this student profile:
        - GPA: ${profile?.gpa_weighted || 'Not specified'}
        - SAT: ${profile?.sat_composite || 'Not taken'}
        - ACT: ${profile?.act_composite || 'Not taken'}
        - State: ${profile?.state || 'Not specified'}
        - Interests: ${profile?.interests || 'Not specified'}
        
        Suggest 5-7 relevant MAJOR scholarships including:
        - National merit-based scholarships ($2,500+)
        - Large foundation scholarships ($5,000+)
        - Subject-specific scholarships ($1,000+)
        - Need-based options if applicable
        
        Focus on substantial scholarships worth $1,000 or more.
        
        For each, provide:
        - Name
        - Amount (estimate if varies)
        - Deadline
        - Key requirements
        - Application website`;
      
      // Call AI API (you'll need to implement this endpoint)
      const response = await fetch('/api/anthropic/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'scholarship-search', prompt, userId: currentUser.id })
      });
      
      if (!response.ok) throw new Error('Failed to get scholarship recommendations');
      
      const data = await response.json();
      const { scholarships, metadata } = data;
      
      // Save scholarships to database
      if (scholarships && scholarships.length > 0) {
        const sessionId = metadata?.generatedAt || new Date().toISOString();
        
        // Save each scholarship to the database
        for (const scholarship of scholarships) {
          const { error } = await supabase
            .from('scholarships')
            .insert({
              user_id: currentUser.id,
              name: scholarship.name,
              provider: scholarship.provider,
              amount: scholarship.amount,
              deadline: scholarship.deadline,
              requirements: scholarship.requirements,
              application_link: scholarship.applicationLink,
              notes: scholarship.notes,
              ai_match_score: scholarship.aiMatchScore,
              match_reason: scholarship.matchReason,
              ai_session_id: sessionId,
              status: 'suggested',
              scholarship_type: categorizeScholarship(scholarship)
            });
          
          if (error) {
            console.error('Error saving scholarship:', error);
          }
        }
        
        // Load all scholarships from database (including previous ones)
        await loadScholarshipsFromDatabase();
      } else {
        setRecommendedScholarships(scholarships || []);
      }
      
    } catch (error) {
      console.error('Error finding scholarships:', error);
      toast.error('Failed to find scholarship recommendations');
      
      // Fallback to sample recommendations
      setRecommendedScholarships(getSampleScholarships());
    } finally {
      setSearchingMajorScholarships(false);
    }
  };
  
  const findSmallScholarshipsWithAI = async () => {
    setSearchingSmallScholarships(true);
    
    try {
      // Get student profile data
      const { data: profile } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', currentUser.id)
        .single();
      
      // Get existing scholarships for context
      const { data: existingScholarships } = await supabase
        .from('scholarships')
        .select('name, status, scholarship_type, tags')
        .eq('user_id', currentUser.id);
      
      // Get user preferences if they exist
      const { data: preferences } = await supabase
        .from('scholarship_preferences')
        .select('*')
        .eq('user_id', currentUser.id)
        .single();
      
      // Build context about existing scholarships
      const existingNames = existingScholarships?.map(s => s.name) || [];
      const interestedTypes = existingScholarships?.filter(s => s.status === 'interested').map(s => s.scholarship_type) || [];
      const notInterestedTypes = existingScholarships?.filter(s => s.status === 'not_interested').map(s => s.scholarship_type) || [];
      
      // Create AI prompt specifically for small scholarships with full context
      const prompt = `Based on this student profile:
        - GPA: ${profile?.gpa_weighted || 'Not specified'}
        - SAT: ${profile?.sat_composite || 'Not taken'}
        - ACT: ${profile?.act_composite || 'Not taken'}
        - State: ${profile?.state || 'Not specified'}
        - Interests: ${profile?.interests || 'Not specified'}
        
        EXISTING SCHOLARSHIP CONTEXT:
        - Scholarships already suggested: ${existingNames.length > 0 ? existingNames.slice(0, 10).join(', ') : 'None yet'}
        - Student has shown interest in: ${interestedTypes.length > 0 ? interestedTypes.join(', ') : 'No preferences yet'}
        - Student marked as not interested: ${notInterestedTypes.length > 0 ? notInterestedTypes.join(', ') : 'None'}
        - User preferences: ${preferences ? 'Has established preferences' : 'No preferences stored yet'}
        
        Find 7-10 NEW SMALL scholarships specifically in the $100-$1,000 range that are NOT already listed above, including:
        - Local community organization scholarships
        - Small business sponsorships
        - High school specific awards
        - Essay contest scholarships
        - Small foundation grants
        - Local club/organization scholarships (Rotary, Lions, etc.)
        - Small merit awards from local colleges
        
        IMPORTANT: Only include scholarships that are:
        - $100 to $1,000 in value
        - NOT full tuition, NOT per-year amounts
        - Have less competition due to smaller amounts
        - Are often overlooked by students
        - NOT already in the existing list above
        - Match the student's interests and avoid types they've rejected
        
        For each, provide:
        - Name
        - Amount (must be under $1,000)
        - Deadline
        - Key requirements
        - Application website if available
        - Why it's a good "easy win" opportunity for this specific student`;
      
      // Call AI API (use same action as regular search, but with specialized prompt)
      const response = await fetch('/api/anthropic/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'scholarship-search', 
          prompt, 
          userId: currentUser.id
        })
      });
      
      if (!response.ok) throw new Error('Failed to get small scholarship recommendations');
      
      const data = await response.json();
      const { scholarships, metadata } = data;
      
      // Save scholarships to database
      if (scholarships && scholarships.length > 0) {
        const sessionId = metadata?.generatedAt || new Date().toISOString();
        
        // Save each scholarship to the database
        for (const scholarship of scholarships) {
          // Double-check that it's actually a small scholarship
          const amount = parseScholarshipAmount(scholarship.amount);
          if (amount > 0 && amount <= 1000) {
            const { error } = await supabase
              .from('scholarships')
              .insert({
                user_id: currentUser.id,
                name: scholarship.name,
                provider: scholarship.provider,
                amount: scholarship.amount,
                deadline: scholarship.deadline,
                requirements: scholarship.requirements,
                application_link: scholarship.applicationLink,
                notes: scholarship.notes,
                ai_match_score: scholarship.aiMatchScore,
                match_reason: scholarship.matchReason,
                ai_session_id: sessionId,
                status: 'suggested',
                scholarship_type: 'local', // Most small scholarships are local
                tags: ['small', 'easy-win']
              });
            
            if (error) {
              console.error('Error saving small scholarship:', error);
            }
          }
        }
        
        // Load all scholarships from database
        await loadScholarshipsFromDatabase();
        toast.success(`Found ${scholarships.filter(s => parseScholarshipAmount(s.amount) <= 1000).length} new small scholarship opportunities!`);
      } else {
        toast.info('No small scholarships found. Try the regular search for larger opportunities.');
      }
      
    } catch (error) {
      console.error('Error finding small scholarships:', error);
      toast.error('Failed to find small scholarship recommendations');
      
      // Fallback to sample small scholarships
      setRecommendedScholarships(getSampleSmallScholarships());
    } finally {
      setSearchingSmallScholarships(false);
    }
  };
  
  const getSampleScholarships = () => [
    {
      name: 'National Merit Scholarship',
      amount: '$2,500',
      deadline: '2025-02-01',
      requirements: 'PSAT/NMSQT Semifinalist or Finalist',
      link: 'https://www.nationalmerit.org/'
    },
    {
      name: 'Coca-Cola Scholars Program',
      amount: '$20,000',
      deadline: '2025-10-31',
      requirements: 'High school senior, 3.0+ GPA, leadership',
      link: 'https://www.coca-colascholarsfoundation.org/'
    },
    {
      name: 'Gates Scholarship',
      amount: 'Full ride',
      deadline: '2025-09-15',
      requirements: 'Pell-eligible, minority, 3.3+ GPA',
      link: 'https://www.thegatesscholarship.org/'
    }
  ];
  
  const getSampleSmallScholarships = () => [
    {
      name: 'Local Rotary Club Student Award',
      amount: '$500',
      deadline: '2025-03-15',
      requirements: 'Local high school student, community service',
      link: 'https://www.rotary.org/',
      match_reason: 'Small local scholarship with less competition'
    },
    {
      name: 'Community Foundation Essay Contest',
      amount: '$250',
      deadline: '2025-02-28',
      requirements: '500-word essay on community involvement',
      link: '#',
      match_reason: 'Quick essay-based scholarship, perfect easy win'
    },
    {
      name: 'Local Business Student Sponsorship',
      amount: '$750',
      deadline: '2025-04-01',
      requirements: 'Local resident, planning to attend college',
      link: '#',
      match_reason: 'Often overlooked by students, good opportunity'
    }
  ];
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  const getDaysUntil = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  const getUrgencyColor = (daysUntil) => {
    if (daysUntil < 0) return 'text-stone-500';
    if (daysUntil <= 7) return 'text-red-600';
    if (daysUntil <= 30) return 'text-orange-600';
    return 'text-emerald-600';
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-azure-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-azure-50 via-white to-emerald-50">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/college')}
                className="p-3 bg-stone-50 hover:bg-stone-100 border border-stone-200 rounded-xl transition-all duration-300 hover:shadow-sm hover:-translate-y-0.5"
              >
                <ArrowLeft className="w-4 h-4 text-stone-600" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
                  <DollarSign className="w-7 h-7 text-emerald-600" />
                  Financial Aid & Scholarships
                </h1>
                <p className="text-sm text-slate-600 mt-1">
                  Track financial aid requirements and discover scholarship opportunities
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Financial Aid Checklist - Only show if on financial-aid view */}
          {activeView === 'financial-aid' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-azure-600" />
                Financial Aid Checklist
              </h2>
              <button
                onClick={() => setAddingCustomTask(!addingCustomTask)}
                className="text-sm text-azure-600 hover:text-azure-700 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Task
              </button>
            </div>
            
            {/* Add custom task form */}
            {addingCustomTask && (
              <div className="mb-4 p-4 bg-stone-50 rounded-lg">
                <input
                  type="text"
                  placeholder="Task title"
                  value={customTaskTitle}
                  onChange={(e) => setCustomTaskTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg mb-2"
                />
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={customTaskDate}
                    onChange={(e) => setCustomTaskDate(e.target.value)}
                    className="flex-1 px-3 py-2 border border-stone-300 rounded-lg"
                  />
                  <button
                    onClick={addCustomTask}
                    className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setAddingCustomTask(false);
                      setCustomTaskTitle('');
                      setCustomTaskDate('');
                    }}
                    className="px-4 py-2 bg-stone-300 text-slate-700 rounded-lg hover:bg-stone-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {/* Task list */}
            <div className="space-y-3">
              {financialAidTasks.length === 0 ? (
                <p className="text-stone-500 text-center py-8">
                  No financial aid tasks yet. Add your first task above.
                </p>
              ) : (
                financialAidTasks.map((task) => {
                  const daysUntil = getDaysUntil(task.due_date);
                  const isComplete = task.workflow_phase === 'complete' || task.completion_status === 'complete';
                  
                  return (
                    <div
                      key={task.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${
                        isComplete ? 'bg-stone-50 border-stone-200' : 'bg-white border-stone-300'
                      } hover:shadow-sm transition-shadow`}
                    >
                      <button
                        onClick={() => toggleTaskCompletion(task)}
                        className="mt-1 flex-shrink-0"
                      >
                        {isComplete ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <Circle className="w-5 h-5 text-stone-400 hover:text-azure-600" />
                        )}
                      </button>
                      
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className={`font-medium ${isComplete ? 'text-stone-500 line-through' : 'text-slate-900'}`}>
                              {task.title}
                            </h3>
                            {task.due_date && (
                              <div className="flex items-center gap-2 mt-1">
                                <Calendar className="w-4 h-4 text-stone-400" />
                                <span className={`text-sm ${getUrgencyColor(daysUntil)}`}>
                                  {formatDate(task.due_date)}
                                  {daysUntil !== null && daysUntil >= 0 && (
                                    <span className="ml-1">({daysUntil} days)</span>
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {task.external_link && (
                            <a
                              href={task.external_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-azure-600 hover:text-azure-700"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            {/* Quick Links */}
            <div className="mt-6 pt-6 border-t border-stone-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Quick Links</h3>
              <div className="grid grid-cols-2 gap-2">
                <a
                  href="https://studentaid.gov/fafsa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-azure-50 text-azure-700 rounded-lg hover:bg-azure-100 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  FAFSA
                </a>
                <a
                  href="https://cssprofile.collegeboard.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-azure-50 text-azure-700 rounded-lg hover:bg-azure-100 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  CSS Profile
                </a>
                <a
                  href="https://idoc.collegeboard.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-azure-50 text-azure-700 rounded-lg hover:bg-azure-100 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  IDOC
                </a>
                <a
                  href="https://www.irs.gov/forms-instructions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-azure-50 text-azure-700 rounded-lg hover:bg-azure-100 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  IRS Forms
                </a>
              </div>
            </div>
          </div>
          )}

          {/* Scholarship Opportunities - Show on both views, but full width if scholarships view */}
          <div className={`bg-white rounded-xl shadow-lg p-6 ${activeView === 'scholarships' ? 'lg:col-span-2' : ''}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-600" />
                Scholarship Opportunities
              </h2>
            </div>
            
            {/* AI Scholarship Finder */}
            <div className="space-y-3 mb-6">
              <button
                onClick={findScholarshipsWithAI}
                disabled={searchingMajorScholarships || searchingSmallScholarships}
                className="w-full px-4 py-3 bg-gradient-to-r from-violet-600 to-azure-600 text-white rounded-lg hover:from-violet-700 hover:to-azure-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
              >
                {searchingMajorScholarships ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Finding Major Scholarships...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Find Major Scholarships with AI
                  </>
                )}
              </button>
              
              <button
                onClick={findSmallScholarshipsWithAI}
                disabled={searchingMajorScholarships || searchingSmallScholarships}
                className="w-full px-4 py-3 bg-gradient-to-r from-emerald-600 to-emerald-600 text-white rounded-lg hover:from-emerald-700 hover:to-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
              >
                {searchingSmallScholarships ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Finding Small Scholarships...
                  </>
                ) : (
                  <>
                    <DollarSign className="w-5 h-5" />
                    Find Small Scholarships ($100-$1,000)
                  </>
                )}
              </button>
            </div>
            
            {/* Recommended Scholarships */}
            {/* Selected Scholarships - Application List */}
            {groupedScholarships.selected.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  Application List ({groupedScholarships.selected.length})
                </h3>
                <div className="space-y-3">
                  {groupedScholarships.selected.map((scholarship) => (
                    <ScholarshipCard
                      key={scholarship.id}
                      scholarship={scholarship}
                      onStatusChange={updateScholarshipStatus}
                      currentStatus="selected"
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Interested Scholarships */}
            {groupedScholarships.interested.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500" />
                  Researching ({groupedScholarships.interested.length})
                </h3>
                <div className="space-y-3">
                  {groupedScholarships.interested.map((scholarship) => (
                    <ScholarshipCard
                      key={scholarship.id}
                      scholarship={scholarship}
                      onStatusChange={updateScholarshipStatus}
                      currentStatus="interested"
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Suggested Scholarships */}
            {groupedScholarships.suggested.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  AI Recommendations ({groupedScholarships.suggested.length})
                </h3>
                <div className="space-y-3">
                  {groupedScholarships.suggested.map((scholarship) => (
                    <ScholarshipCard
                      key={scholarship.id}
                      scholarship={scholarship}
                      onStatusChange={updateScholarshipStatus}
                      currentStatus="suggested"
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Low-Dollar Scholarships Section */}
            {groupedScholarships.lowDollar.length > 0 && (
              <div className="mb-6">
                <div className="bg-gradient-to-r from-emerald-50 to-azure-50 rounded-lg p-4 mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-lg font-bold text-slate-900">
                      Don't Miss Out: Smaller Scholarships
                    </h3>
                  </div>
                  <p className="text-sm text-slate-700 mb-1">
                    <strong>{groupedScholarships.lowDollar.length}</strong> scholarships under $1,000 that many students overlook.
                  </p>
                  <p className="text-xs text-slate-600">
                    💡 <strong>Pro tip:</strong> These "smaller" scholarships add up and have less competition!
                  </p>
                </div>
                <div className="space-y-3">
                  {groupedScholarships.lowDollar.map((scholarship) => (
                    <div key={scholarship.id} className="relative">
                      <ScholarshipCard
                        scholarship={scholarship}
                        onStatusChange={updateScholarshipStatus}
                        currentStatus={scholarship.status}
                      />
                      <div className="absolute top-2 left-2">
                        <span className="bg-emerald-100 text-emerald-800 text-xs font-medium px-2 py-1 rounded-full">
                          Easy Win!
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Show message if no scholarships */}
            {recommendedScholarships.length === 0 && 
             groupedScholarships.selected.length === 0 &&
             groupedScholarships.interested.length === 0 &&
             groupedScholarships.suggested.length === 0 &&
             groupedScholarships.lowDollar.length === 0 && (
              <div className="text-center py-8 text-stone-500">
                <Sparkles className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p>No scholarships found yet.</p>
                <p className="text-sm mt-1">Click "Find Scholarships with AI" to get started!</p>
              </div>
            )}
            
            {/* Tracked Scholarships */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Your Tracked Scholarships
              </h3>
              {scholarships.length === 0 ? (
                <div className="text-center py-8 bg-stone-50 rounded-lg">
                  <Award className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500 text-sm">
                    No scholarships tracked yet.
                  </p>
                  <p className="text-stone-400 text-xs mt-1">
                    Use AI to find relevant scholarships
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {scholarships.map((scholarship) => (
                    <div
                      key={scholarship.id}
                      className="p-3 bg-stone-50 rounded-lg border border-stone-200"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-slate-900">{scholarship.scholarship_name}</h4>
                          <div className="flex items-center gap-4 mt-1 text-sm">
                            <span className="text-emerald-600">{scholarship.amount}</span>
                            <span className="text-stone-500">Due: {formatDate(scholarship.deadline)}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              scholarship.status === 'submitted' ? 'bg-emerald-100 text-emerald-700' :
                              scholarship.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-stone-100 text-slate-700'
                            }`}>
                              {scholarship.status}
                            </span>
                          </div>
                        </div>
                        {scholarship.application_link && (
                          <a
                            href={scholarship.application_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-azure-600 hover:text-azure-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Stats Summary */}
            <div className="mt-6 pt-6 border-t border-stone-200">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-emerald-600">
                    ${scholarships.reduce((sum, s) => {
                      const amount = parseInt(s.amount?.replace(/[^0-9]/g, '') || 0);
                      return sum + (s.status === 'awarded' ? amount : 0);
                    }, 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-stone-500 mt-1">Awarded</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-azure-600">
                    {scholarships.filter(s => s.status === 'submitted').length}
                  </p>
                  <p className="text-xs text-stone-500 mt-1">Submitted</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-600">
                    {scholarships.filter(s => s.status === 'in_progress').length}
                  </p>
                  <p className="text-xs text-stone-500 mt-1">In Progress</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* College-Specific Financial Aid Status */}
        {colleges && colleges.length > 0 && (
          <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-azure-600" />
              College-Specific Financial Aid Status
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {colleges.filter(c => c.category !== 'considering').map((college) => (
                <div key={college.id} className="p-4 bg-stone-50 rounded-lg">
                  <h3 className="font-medium text-slate-900 mb-2">{college.name}</h3>
                  <div className="space-y-1 text-sm">
                    {college.finaid_deadline && (
                      <p className="text-slate-600">
                        Aid Deadline: {formatDate(college.finaid_deadline)}
                      </p>
                    )}
                    {college.css_deadline && (
                      <p className="text-slate-600">
                        CSS Profile: {formatDate(college.css_deadline)}
                      </p>
                    )}
                    <p className={`font-medium ${
                      college.financial_aid_status === 'complete' ? 'text-emerald-600' :
                      college.financial_aid_status === 'in_progress' ? 'text-yellow-600' :
                      'text-stone-400'
                    }`}>
                      Status: {college.financial_aid_status || 'Not Started'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialAidHub;